// ACT-1247 — resolve a loaded Pathways member to Provider Portal's RmiMembId.
//
// Create handoffs have no source auth, so PP cannot resolve the member from an
// authNo (the way Duplicate does). PP's member lookup keys off its OWN MembId
// (RmiMembId), which is NOT the same value as Pathways' memberPolicyId (a
// policy-surrogate id). Minting a Create handoff with memberPolicyId leaves PP
// unable to resolve the member.
//
// This reuses the exact crosswalk the Documents flow uses (see
// MemberDocumentsPage's `ppMatch`): search PP by demographics (name + DOB)
// scoped to the member's IPA, then disambiguate to a single record by IPA.
// Here the same logic runs imperatively (client.query) so it can be awaited
// inside the Create click handler before minting.

import type { ApolloClient } from "@apollo/client";

import {
  CONNECT_PATIENT_IDS,
  selectHealthPlanMemberId,
  type ConnectPatientIdsResponse,
  type ConnectPatientIdsVars,
} from "./graphql/connectPatientIds";
import {
  SEARCH_PROVIDER_PORTAL_MEMBERS,
  type ProviderPortalMember,
  type SearchProviderPortalMembersResponse,
  type SearchProviderPortalMembersVars,
} from "./graphql/providerPortalSearch";

/** The minimal shape of a loaded Pathways member this resolver needs. */
export interface LoadedMemberForResolve {
  name?: string | null;
  dob?: string | null;
  ipaCode?: string | null;
  /**
   * PP's `RmiMembKeyId` for this member — carried by a Pathways member as
   * `memberIdOthers.apolloMemberKey` (the same value `toAuthMember` already
   * sends as `RmiMembKeyId`, and the same key `toPatientObject` passes as
   * `mkey`). When present it resolves the member EXACTLY, so the demographic
   * crosswalk below is only a fallback.
   */
  memberKeyId?: string | null;
  /**
   * PP's own `RmiMembId`, when the caller already has it — a PP-path member
   * carries it on the search row, and MemberProfilePage recovers it on a hard
   * refresh via the same CONNECT_PATIENT_IDS point lookup used below.
   *
   * Consumed ONLY by `resolvePpMemberRecord`, which needs the whole PP row and
   * can fetch it directly by this id (`GetMembersForSearch` matches
   * `RmiMembId = @MemberId` EXACTLY, not by prefix). `resolvePpMembId` ignores
   * it — for that function this value IS the answer, so a caller who has it has
   * no reason to call.
   */
  membId?: string | null;
}

/**
 * Split a Pathways member display name into first/last for the PP search.
 * Handles both "Last, First" and "First [Middle] Last".
 */
export function parsePathwaysName(
  name: string | undefined | null,
): { firstName: string; lastName: string } | null {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return null;
  if (trimmed.includes(",")) {
    const [last, first] = trimmed.split(",", 2).map((s) => s.trim());
    if (!last || !first) return null;
    return { firstName: first.split(/\s+/)[0]!, lastName: last };
  }
  const tokens = trimmed.split(/\s+/);
  if (tokens.length < 2) return null;
  // Preserve everything after the first token as the last name so compound
  // surnames ("Maria Van Der Berg") and suffixes ("Robert Smith Jr") are not
  // truncated to a single trailing token.
  return { firstName: tokens[0]!, lastName: tokens.slice(1).join(" ") };
}

/**
 * Normalize a name for comparison: lowercase, trim, collapse internal
 * whitespace, and strip diacritics. Used to re-verify that a DOB-matched PP row
 * actually belongs to the searched person before resolving.
 */
function normalizeName(value: string | undefined | null): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/** Normalize a date string (MM/DD/YYYY or ISO-ish) to YYYY-MM-DD. */
export function toIsoDate(value: string | undefined | null): string | undefined {
  if (!value) return undefined;
  const us = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value);
  if (us) return `${us[3]}-${us[1]}-${us[2]}`;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return iso ? iso[1] : undefined;
}

/**
 * Compare two date strings by calendar day. Both sides must parse via
 * `toIsoDate` (the ONLY path — no `new Date(...)` fallback, which can shift the
 * calendar day in a non-UTC runtime). An unparseable date is treated as a
 * non-match, the safe default for write-side member resolution.
 */
function sameDay(a: string, b: string): boolean {
  const da = toIsoDate(a);
  const db = toIsoDate(b);
  if (!da || !db) return false;
  return da === db;
}

/** Normalized and space-stripped, matching PP's `REPLACE(UPPER(x), ' ', '')`. */
function compactName(value: string | undefined | null): string {
  return normalizeName(value).replace(/ /g, "");
}

/**
 * True when a PP row's name is consistent with the name we actually searched
 * for. This deliberately MIRRORS PP's own SQL name predicates rather than
 * imposing a stricter rule of its own, because that is the only thing this
 * re-verification can soundly assert: PP returned this row, so the question is
 * "is it a row matching my query?", not "can I re-derive the identity myself".
 *
 * PP's `GetMembersForSearch` space-strips both sides and PREFIX-matches whenever
 * a DOB or MemberId is supplied (and one always is here — a DOB is required
 * below):
 *
 *     REPLACE(UPPER(RmiFirstName), ' ', '') LIKE REPLACE(UPPER(@FirstName), ' ', '') + '%'
 *
 * Two real skews follow from that, and both were false-negatived by the exact
 * equality this replaced:
 *   - PP stores given + middle in `RmiFirstName` ("HSIA KANG") while a Pathways
 *     display name carries the given name alone ("Hsia Hsu") — the reported bug,
 *     which blocked Create Auth for every member with a middle name in PP.
 *   - PP stores a compound/married surname ("CRUZ SANTOS") where Pathways has
 *     the short form ("Cruz") — the same failure, one field over.
 *
 * It stays strict in the direction that matters: a row whose name is NOT a
 * prefix-extension of the search is refused ("CRUZ" is not a match for a search
 * of "Dela Cruz"), so two different people cannot be conflated here. Identity
 * safety proper still rests on the untouched gates in `pickPpMatch` — exact DOB,
 * IPA scoping, and refusing ambiguity.
 */
function rowMatchesName(
  row: ProviderPortalMember,
  nameParts: { firstName: string; lastName: string },
): boolean {
  const wantFirst = compactName(nameParts.firstName);
  const wantLast = compactName(nameParts.lastName);
  if (!wantFirst || !wantLast) return false;
  const rowFirst = compactName(row.firstName);
  const rowLast = compactName(row.lastName);
  if (rowFirst && rowLast) {
    return rowFirst.startsWith(wantFirst) && rowLast.startsWith(wantLast);
  }
  // Fall back to fullName when the row lacks split first/last fields. It is
  // "First [Middle] Last" ordered (a pre-existing assumption of this crosswalk),
  // so the searched given name sits at the front and the surname at the end;
  // any middle name PP carries lands harmlessly between them.
  const rowFull = compactName(row.fullName);
  if (!rowFull) return false;
  return rowFull.startsWith(wantFirst) && rowFull.endsWith(wantLast);
}

/**
 * Disambiguate PP search rows to a single member, mirroring the Documents
 * `ppMatch` logic but hardened for write-side resolution: a DOB is REQUIRED, the
 * DOB filter excludes rows with a missing/unparseable dob, there is NO unfiltered
 * fallback, and each surviving row's name is re-verified against the searched
 * name. Then prefer the row in the member's own IPA. Returns null when no row
 * survives or the match is ambiguous — callers must never guess which person to
 * attach an auth to.
 */
export function pickPpMatch(
  rows: readonly ProviderPortalMember[],
  dobIso: string | undefined,
  ipaCode: string | null | undefined,
  nameParts: { firstName: string; lastName: string },
): ProviderPortalMember | null {
  const candidates = ppCandidates(rows, dobIso, ipaCode, nameParts);
  return candidates ? pickUniquePpCandidate(candidates) : null;
}

/**
 * The surviving candidate rows for a demographic search, shared by BOTH pickers
 * (`pickPpMatch` for Create Auth's id resolution and `pickPpMemberRecord` for
 * Create Claim's whole-row resolution) so the identity-safety filters exist in
 * exactly one place. Returns null when nothing survives.
 */
interface PpCandidates {
  /** Rows matching DOB + name (and therefore the same person, modulo IPA). */
  pool: ProviderPortalMember[];
  /** The subset in the member's own IPA. */
  sameIpa: ProviderPortalMember[];
  /** False when the member is not scoped to an IPA (missing / "NA"). */
  isScoped: boolean;
}

function ppCandidates(
  rows: readonly ProviderPortalMember[],
  dobIso: string | undefined,
  ipaCode: string | null | undefined,
  nameParts: { firstName: string; lastName: string },
): PpCandidates | null {
  if (rows.length === 0) return null;
  // Require a DOB — refuse to resolve on name + IPA alone.
  if (!dobIso) return null;
  // Exclude rows with a null/unparseable dob; an empty result IS a no-match
  // (no unfiltered fallback).
  const dobFiltered = rows.filter((r) => r.dob != null && sameDay(r.dob, dobIso));
  // Re-verify the name so a same-DOB row for a DIFFERENT person can't slip
  // through to IPA disambiguation.
  const pool = dobFiltered.filter((r) => rowMatchesName(r, nameParts));
  if (pool.length === 0) return null;
  // "NA"/empty ipaCode means the member is not scoped to an IPA (consistent with
  // resolvePpMembId, which omits companyId from the search in that case).
  const isScoped = !!ipaCode && ipaCode !== "NA";
  const sameIpa = pool.filter((r) => r.companyId === ipaCode);
  return { pool, sameIpa, isScoped };
}

/**
 * The historical "exactly one row" rule, unchanged: prefer the lone row in the
 * member's own IPA; accept a lone row only when there is NO IPA constraint;
 * refuse everything else.
 */
function pickUniquePpCandidate(c: PpCandidates): ProviderPortalMember | null {
  if (c.sameIpa.length === 1) return c.sameIpa[0]!;
  // Only accept a lone surviving row when there is NO IPA constraint. When an
  // ipaCode IS set, a single row from a DIFFERENT IPA must be refused — minting
  // an auth against the wrong member is a clinical-safety risk.
  if (c.pool.length === 1 && !c.isScoped) return c.pool[0]!;
  // Cross-IPA single match or genuinely ambiguous — never guess which person
  // this auth belongs to.
  return null;
}

/**
 * Identity of the PERSON a PP row describes, for telling "two coverage spans of
 * one member" apart from "two different members". Null when the row carries no
 * usable identity at all (such a row can never be safely collapsed).
 */
function ppRowIdentity(row: ProviderPortalMember): string | null {
  const norm = (v: string | null | undefined) => (v ?? "").trim().toUpperCase();
  const key = norm(row.membKeyId);
  const id = norm(row.membId);
  if (!key && !id) return null;
  return `${norm(row.companyId)}|${key}|${id}`;
}

/**
 * Collapse several rows for the SAME member to the one describing their most
 * recent coverage span.
 *
 * WHY THIS IS NEEDED AND WHY IT IS NOT AN IDENTITY DECISION. `vRmiMemberSearch`
 * is `RmiMember × RmiMembHpHist × RmiMembPcpHist` — one row per (health-plan
 * span × PCP span) — and PP-API's `GetMembersForSearch` applies no DISTINCT,
 * keeping every span that is open or ended within the last 730 days. So a member
 * who changed PCP or health plan recently legitimately comes back as several
 * rows. Every member-level column (name, DOB, sex, ids) is identical across
 * them — they all come from `RmiMember` — so choosing between them is choosing a
 * SPAN, not a person. That is why this is safe here while `pickUniquePpCandidate`
 * still refuses ambiguity: it cannot tell the two cases apart, and this can
 * (`ppRowIdentity`).
 *
 * It matters because `spRmiCreateTempClaim` selects the member by the COMPOSITE
 * `(RmiMembKeyId, RmiCompanyId, RmiStartDate, RmiHpCode)` and INSERTs regardless
 * — a composite miss yields an HTTP 200 with a blank-patient claim shell — so the
 * caller needs one real row's values, together, not an approximation.
 *
 * Refuses (returns null) when: the rows describe more than one person, any row
 * has no usable identity, no row has a parseable `startDate`, or the most recent
 * span is shared by more than one health plan (the plan rides on the claim, so
 * two plans starting the same day is a real ambiguity, not a formality).
 */
function pickCurrentSpanRow(
  rows: readonly ProviderPortalMember[],
): ProviderPortalMember | null {
  if (rows.length === 0) return null;
  if (rows.length === 1) return rows[0]!;
  const identities = rows.map(ppRowIdentity);
  if (identities.some((i) => i === null)) return null;
  if (new Set(identities).size !== 1) return null;
  const dated = rows
    .map((row) => ({ row, iso: toIsoDate(row.startDate) }))
    .filter((r): r is { row: ProviderPortalMember; iso: string } => !!r.iso);
  if (dated.length === 0) return null;
  const latestIso = dated.reduce((max, r) => (r.iso > max ? r.iso : max), dated[0]!.iso);
  const latest = dated.filter((r) => r.iso === latestIso).map((r) => r.row);
  const plans = new Set(
    latest.map((r) => (r.healthPlanCode ?? "").trim().toUpperCase()),
  );
  if (plans.size !== 1) return null;
  // Same person, same span start, same plan — the whole composite key is
  // identical, so these rows are interchangeable for this purpose.
  return latest[0]!;
}

/**
 * Disambiguate PP search rows to a single WHOLE member row (not just its id).
 *
 * Identity discipline is exactly `pickPpMatch`'s — same DOB requirement, same
 * name re-verification, same IPA scoping, same refusal to choose between two
 * people — and then, only for rows that provably describe ONE person, the most
 * recent coverage span is chosen (see `pickCurrentSpanRow`). Create Claim needs a
 * row rather than an id because `membKeyId`, `healthPlanCode` and `startDate`
 * only form a valid EZCap composite key when they come from the SAME row.
 */
export function pickPpMemberRecord(
  rows: readonly ProviderPortalMember[],
  dobIso: string | undefined,
  ipaCode: string | null | undefined,
  nameParts: { firstName: string; lastName: string },
): ProviderPortalMember | null {
  const candidates = ppCandidates(rows, dobIso, ipaCode, nameParts);
  if (!candidates) return null;
  const unique = pickUniquePpCandidate(candidates);
  if (unique) return unique;
  // A scoped member's rows from OTHER IPAs are never collapsible — refusing them
  // is the whole point of `pickUniquePpCandidate`'s cross-IPA rule.
  if (candidates.isScoped && candidates.sameIpa.length === 0) return null;
  return pickCurrentSpanRow(candidates.isScoped ? candidates.sameIpa : candidates.pool);
}

/**
 * Disambiguate the rows of an EXACT-id PP search (`RmiMembId = @MemberId`) to a
 * single whole row. There is no name to re-verify here — the id came from the
 * member's own record via a point lookup, not from a demographic guess — but the
 * DOB is still cross-checked whenever it is known, because a row that disagrees
 * on DOB means the crosswalk itself is wrong and must not be trusted.
 */
function pickPpRecordByMembId(
  rows: readonly ProviderPortalMember[],
  membId: string,
  dobIso: string | undefined,
): ProviderPortalMember | null {
  const wanted = membId.trim().toUpperCase();
  if (!wanted) return null;
  const exact = rows.filter((r) => (r.membId ?? "").trim().toUpperCase() === wanted);
  const verified = dobIso
    ? exact.filter((r) => r.dob != null && sameDay(r.dob, dobIso))
    : exact;
  return pickCurrentSpanRow(verified);
}

/** Thrown when no single PP record can be resolved for a Create handoff. */
export class PpMemberResolutionError extends Error {
  constructor(member: LoadedMemberForResolve) {
    const name = member.name?.trim();
    const dob = toIsoDate(member.dob);
    super(
      "Could not start a new authorization: no matching Provider Portal " +
        "record was found" +
        (name ? ` for ${name}` : "") +
        (dob ? ` (DOB ${dob})` : "") +
        ".",
    );
    this.name = "PpMemberResolutionError";
  }
}

/**
 * Resolve PP's RmiMembId from the member's OWN PP member key (mkey) via the
 * `connectPatient` point lookup — the deterministic crosswalk, with no
 * demographic guessing. This is the same point lookup `@astrana/ui`'s
 * `usePatientProfile` runs internally to resolve the profile header off
 * `(companyId && mkey)`, and the same one MemberProfilePage already uses to
 * recover `membId` on a hard-refreshed PP profile (see CONNECT_PATIENT_IDS).
 *
 * Returns `{}` when there is no key to look up, the lookup yields nothing, or it
 * errors — in every one of those cases the caller falls back to the name + DOB
 * crosswalk rather than failing the auth outright. A swallowed `error` is
 * reported back (not thrown) so a caller that ALSO fails its fallback can tell
 * "no such record" apart from "PP was unreachable".
 */
async function resolveByMemberKey(
  client: ApolloClient<unknown>,
  memberKeyId: string | null | undefined,
  ipaCode: string | undefined,
): Promise<{ membId?: string; error?: unknown }> {
  if (!memberKeyId) return {};
  try {
    const { data } = await client.query<
      ConnectPatientIdsResponse,
      ConnectPatientIdsVars
    >({
      query: CONNECT_PATIENT_IDS,
      variables: {
        // Upper-cased for the same reason MemberProfilePage upper-cases `ppMkey`
        // before this very lookup: a raw-vs-upper-cased mkey silently never
        // matches, so the lookup would return null and look like "no record".
        memberKeyId: memberKeyId.toUpperCase(),
        ipaId: ipaCode,
      },
      fetchPolicy: "network-only",
    });
    // Trim before testing: a blank/whitespace id must fall through to the
    // demographic crosswalk, not be returned as a resolved membId.
    return {
      membId: selectHealthPlanMemberId(data, ipaCode)?.trim() || undefined,
    };
  } catch (error) {
    // Deliberately non-fatal — fall through to the demographic crosswalk. Logged
    // because otherwise an auth/session failure on this path is indistinguishable
    // from a genuine "no record" once the fallback also fails.
    console.warn("[resolvePpMembId] connectPatient point lookup failed", error);
    return { error };
  }
}

/** The IPA to scope a PP search to, or undefined when the member has none. */
function searchIpaCode(member: LoadedMemberForResolve): string | undefined {
  return member.ipaCode && member.ipaCode !== "NA" ? member.ipaCode : undefined;
}

/**
 * The name + DOB crosswalk search itself, shared by `resolvePpMembId` and
 * `resolvePpMemberRecord` so there is one query, one variable set and one place
 * where the IPA scoping is decided. Returns null (no query fired) when the
 * member's name cannot be split into first + last, which is the one input this
 * search cannot be run without.
 */
async function searchPpByDemographics(
  client: ApolloClient<unknown>,
  member: LoadedMemberForResolve,
): Promise<{
  rows: ProviderPortalMember[];
  dobIso: string | undefined;
  nameParts: { firstName: string; lastName: string };
} | null> {
  const nameParts = parsePathwaysName(member.name);
  if (!nameParts) return null;
  const dobIso = toIsoDate(member.dob);
  const { data } = await client.query<
    SearchProviderPortalMembersResponse,
    SearchProviderPortalMembersVars
  >({
    query: SEARCH_PROVIDER_PORTAL_MEMBERS,
    variables: {
      input: {
        firstName: nameParts.firstName,
        lastName: nameParts.lastName,
        ...(dobIso ? { dateOfBirth: dobIso } : {}),
        // Scope to the member's own IPA (forwarded as X-Backend-Id), exactly as
        // the Documents crosswalk does — required for SSO/service-account
        // sessions whose token groups don't map to IPAs in prod.
        ...(searchIpaCode(member) ? { companyId: searchIpaCode(member) } : {}),
      },
    },
    fetchPolicy: "network-only",
  });
  return { rows: data?.searchProviderPortalMembers ?? [], dobIso, nameParts };
}

/**
 * Fetch a member's PP rows by their PP `RmiMembId`. `GetMembersForSearch` matches
 * that column EXACTLY (`RmiMembId = @MemberId`, unlike the name predicates), so
 * this is a point lookup — but it can still return SEVERAL rows for the one
 * member, one per coverage span (see `pickCurrentSpanRow`).
 */
async function searchPpByMembId(
  client: ApolloClient<unknown>,
  membId: string,
  ipaCode: string | undefined,
): Promise<ProviderPortalMember[]> {
  const { data } = await client.query<
    SearchProviderPortalMembersResponse,
    SearchProviderPortalMembersVars
  >({
    query: SEARCH_PROVIDER_PORTAL_MEMBERS,
    variables: {
      input: { memberId: membId, ...(ipaCode ? { companyId: ipaCode } : {}) },
    },
    fetchPolicy: "network-only",
  });
  return data?.searchProviderPortalMembers ?? [];
}

/**
 * Resolve a loaded Pathways member to Provider Portal's RmiMembId (its
 * `membId`). Prefers the member's own PP member key (an exact point lookup) and
 * falls back to the demographic name + DOB search only when no key is available
 * or it resolves nothing. Throws PpMemberResolutionError when neither path
 * yields a single confident match, so the caller fails loud instead of minting a
 * Create handoff with the wrong id.
 */
export async function resolvePpMembId(
  client: ApolloClient<unknown>,
  member: LoadedMemberForResolve,
): Promise<string> {
  // Preferred: the member's own mkey resolves RmiMembId exactly, immune to the
  // name/DOB skew between the two systems' member records.
  const byMemberKey = await resolveByMemberKey(
    client,
    member.memberKeyId,
    searchIpaCode(member),
  );
  if (byMemberKey.membId) return byMemberKey.membId;

  const search = await searchPpByDemographics(client, member);
  if (!search) throw new PpMemberResolutionError(member);

  const match = pickPpMatch(
    search.rows,
    search.dobIso,
    member.ipaCode,
    search.nameParts,
  );
  if (!match?.membId) throw new PpMemberResolutionError(member);
  return match.membId;
}

/**
 * Resolve a loaded member to their WHOLE Provider Portal member row — the row
 * `vRmiMemberSearch` describes, carrying `membKeyId`, `healthPlanCode` and
 * `startDate` TOGETHER.
 *
 * WHY A ROW AND NOT THREE FIELDS. `spRmiCreateTempClaim` finds the member by the
 * composite `RmiMembKeyId = @MembKeyId AND RmiCompanyId = @CompanyId AND
 * convert(varchar, RmiStartDate, 112) = convert(varchar, @StartDate, 112) AND
 * RmiHpCode = @HpCode`, and then INSERTs whether or not that matched — a miss
 * leaves every `@MEMB*` value NULL and still answers HTTP 200, i.e. a silent
 * blank-patient claim shell. `RmiStartDate` is itself a COMPUTED per-span value
 * (`MAX(RmiOpFromDt, RmiPcpFromDt)`), so no other system's coverage-start date
 * (Mongo `enrollmentDate`, connect-gql `insurance.effectiveDate`) can be
 * substituted for it, and three values assembled from different sources cannot
 * form a matching composite. Only one real row will do.
 *
 * Resolution order — most deterministic first, each falling through to the next:
 *   1. A `membId` the caller already holds → exact-id search.
 *   2. The member's own mkey → `connectPatient` point lookup → exact-id search.
 *   3. The name + DOB crosswalk, with `resolvePpMembId`'s identity discipline.
 *
 * Returns null when no single row can be resolved — the caller MUST treat that as
 * "cannot create a claim", never as license to guess. Throws only when PP itself
 * could not be reached (so a caller can distinguish "no record" from "the lookup
 * failed"); an error on an earlier step is deferred and only surfaced if every
 * later step also comes up empty.
 */
export async function resolvePpMemberRecord(
  client: ApolloClient<unknown>,
  member: LoadedMemberForResolve,
): Promise<ProviderPortalMember | null> {
  const ipaCode = searchIpaCode(member);
  const dobIso = toIsoDate(member.dob);
  let deferredError: unknown;

  const byId = async (membId: string): Promise<ProviderPortalMember | null> => {
    try {
      return pickPpRecordByMembId(await searchPpByMembId(client, membId, ipaCode), membId, dobIso);
    } catch (error) {
      // Non-fatal here for the same reason the mkey point lookup is: the
      // demographic crosswalk below is an independent path to the same row. The
      // error is remembered so it can still be reported if that path also fails.
      console.warn("[resolvePpMemberRecord] PP member id lookup failed", error);
      deferredError = error;
      return null;
    }
  };

  const known = member.membId?.trim();
  if (known) {
    const row = await byId(known);
    if (row) return row;
  }

  const byMemberKey = await resolveByMemberKey(client, member.memberKeyId, ipaCode);
  if (byMemberKey.error) deferredError = byMemberKey.error;
  if (byMemberKey.membId && byMemberKey.membId !== known) {
    const row = await byId(byMemberKey.membId);
    if (row) return row;
  }

  // Deliberately NOT wrapped: this is the last resort, so a transport failure
  // here must surface as an error rather than as a reachable-but-empty
  // "no such member" (which reads to the user as a data problem, not an outage).
  const search = await searchPpByDemographics(client, member);
  if (search) {
    const row = pickPpMemberRecord(
      search.rows,
      search.dobIso,
      member.ipaCode,
      search.nameParts,
    );
    if (row) return row;
    // A completed search that matched nothing is a real answer, and a better one
    // than an earlier step's transport failure.
    return null;
  }

  if (deferredError) throw deferredError;
  return null;
}
